import { forwardRef } from 'react';

import { Separator as RadixSeparator, useThemeContext } from '@radix-ui/themes';
import type { RadixSeparatorProps } from '@/types/radix-ui';
import clsx from 'clsx';
import { resolveColor as resolveColorUtil } from '@/utils/colorUtils';

export type DividerOrientation = 'horizontal' | 'vertical';
export type DividerColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'destructive'
  | 'orange'
  | 'blue'
  | 'red'
  | 'green'
  | 'gray'
  | 'teal';
export type DividerSize = 'small' | 'medium' | 'large' | 'full';

export interface DividerProps
  extends Omit<RadixSeparatorProps, 'color' | 'size' | 'orientation'> {
  orientation?: DividerOrientation;
  color?: DividerColor;
  size?: DividerSize;
  className?: string;
}

function resolveColor(
  color: DividerColor,
  accentColor?: string
): RadixSeparatorProps['color'] {
  switch (color) {
    case 'secondary':
      return 'gray';
    case 'default':
      return 'gray';
    default:
      return resolveColorUtil<RadixSeparatorProps['color']>(color, accentColor);
  }
}

export const Divider = forwardRef<HTMLDivElement, DividerProps>(
  (
    {
      orientation = 'horizontal',
      color = 'default',
      size = 'full',
      className,
      ...props
    },
    ref
  ) => {
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const mappedColor = resolveColor(color, accentColor);
    const sizeMapping = {
      small: '1',
      medium: '2',
      large: '3',
      full: '4',
    } as const;
    return (
      <RadixSeparator
        ref={ref}
        orientation={orientation}
        color={mappedColor}
        size={sizeMapping[size]}
        className={clsx(
          'divider',
          orientation === 'horizontal' ? 'w-full' : 'h-full',
          className
        )}
        {...props}
      />
    );
  }
);
Divider.displayName = 'Divider';
